/**
 * Ensure a timestamp string is treated as UTC by appending "Z" when no
 * timezone indicator is present.  Backend timestamps often omit the suffix.
 */
export function normalizeTimestamp(ts) {
    if (ts.endsWith('Z') || ts.includes('+'))
        return ts;
    return ts + 'Z';
}
/**
 * Parse datetime string in user's local timezone.
 *
 * Handles two cases:
 * 1. Datetime with explicit timezone (e.g., "2025-11-06T00:00:00+09:00" or "...Z")
 *    -> Uses standard Date parsing (already correct)
 * 2. Datetime without timezone (e.g., "2025-11-06T00:00:00")
 *    -> Interprets as local browser timezone (not UTC)
 *
 * This fixes the bug where "today" in Korea (UTC+9) was interpreted as GMT,
 * causing a 9-hour gap in history search results.
 *
 * @param dateTimeStr - ISO 8601 datetime string with or without timezone info
 * @returns Milliseconds since epoch in the user's timezone
 */
export function parseInUserTimezone(dateTimeStr) {
    // Check if datetime has explicit timezone information
    const hasExplicitTimezone = /[+-]\d{2}:?\d{2}|Z$/i.test(dateTimeStr);
    if (hasExplicitTimezone) {
        // Has timezone info - JavaScript Date will parse correctly
        return new Date(dateTimeStr).getTime();
    }
    // No timezone info - this is ambiguous
    // JavaScript's Date() constructor treats ISO strings without TZ as UTC
    // But the backend created this in user's timezone
    // We need to interpret it as local time instead
    // Extract date/time components from ISO string
    const isoMatch = dateTimeStr.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    if (!isoMatch) {
        // Unexpected format - fall back to standard parsing
        return new Date(dateTimeStr).getTime();
    }
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    const day = Number(isoMatch[3]);
    const hour = Number(isoMatch[4]);
    const minute = Number(isoMatch[5]);
    const second = Number(isoMatch[6]);
    // Create Date using component constructor
    // This interprets components in the browser's local timezone
    const localDate = new Date(year, month - 1, day, hour, minute, second);
    return localDate.getTime();
}
/**
 * Format a date string as a short localized date (e.g. "Jan 1, 2026").
 * Displays in the user's local timezone.
 */
function stripLeadingLiteral(parts) {
    const first = parts[0];
    return (first?.type === 'literal' ? parts.slice(1) : parts).map((p) => p.value).join('');
}
/**
 * Format a future ISO timestamp as a human-readable relative time string
 * (e.g. "in 5 minutes", "in 2 hours 30 minutes", "in 3 days").
 *
 * Uses `Intl.RelativeTimeFormat` for locale-aware output.
 * Returns "in 0 seconds" if the target is in the past.
 */
export function timeUntil(target, locale, options = {}) {
    const { style = 'long', nowMs = Date.now() } = options;
    const diff = new Date(target).getTime() - nowMs;
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always', style });
    if (diff <= 0)
        return rtf.format(0, 'seconds');
    const totalMinutes = diff / 60_000;
    const ceilMinutes = Math.ceil(totalMinutes);
    if (ceilMinutes < 60)
        return rtf.format(ceilMinutes, 'minutes');
    if (ceilMinutes === 60)
        return rtf.format(1, 'hours');
    let hours = Math.floor(diff / 3_600_000);
    const minutes = Math.ceil((diff % 3_600_000) / 60_000);
    if (minutes === 60) {
        hours += 1;
    }
    if (hours < 24) {
        if (minutes === 0 || minutes === 60)
            return rtf.format(hours, 'hours');
        const hParts = rtf.formatToParts(hours, 'hours');
        const mParts = rtf.formatToParts(minutes, 'minutes');
        return hParts.map((p) => p.value).join('') + ' ' + stripLeadingLiteral(mParts);
    }
    return rtf.format(Math.ceil(diff / 86_400_000), 'days');
}
//# sourceMappingURL=timestamps.js.map